"use client";

import { useEffect, useMemo, useState } from "react";
import {
  IoClose,
  IoInformationCircleOutline,
} from "react-icons/io5";
import mainAPI from "../../../services/main/mainAPI";
import CapacityOverview from "../../shared/CapacityOverview";

type GameRankingRow = {
  id: number | string;
  uuid: string;
  teacherUuid: string;
  exerciseName: string;
  studentPlayedCount: number;
  capacityPercent: {
    skillListeningSpeaking: number | null;
    skillLangNature: number | null;
    skillReading: number | null;
    skillWriting: number | null;
  };
};

const TOP_LIST_LIMIT = 15;
const POPUP_PAGE_SIZE = 20;

function buildPaginationItems(currentPage: number, totalPages: number): Array<number | "ellipsis"> {
  if (totalPages <= 6) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, 4, "ellipsis", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "ellipsis", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages];
}

function normalizeRankingRows(response: unknown): GameRankingRow[] {
  const sourceRows = Array.isArray(response)
    ? response
    : Array.isArray((response as { data?: unknown[] })?.data)
      ? (response as { data: unknown[] }).data
      : [];

  return sourceRows
    .map((item) => {
      const row = item as {
        id?: unknown;
        uuid?: unknown;
        teacher_uuid?: unknown;
        teacherUuid?: unknown;
        exercise_name?: unknown;
        exerciseName?: unknown;
        student_played_count?: unknown;
        studentPlayedCount?: unknown;
        capacity_percent?: {
          skill_listening_speaking?: unknown;
          skill_lang_nature?: unknown;
          skill_reading?: unknown;
          skill_writing?: unknown;
        };
        capacityPercent?: {
          skillListeningSpeaking?: unknown;
          skillLangNature?: unknown;
          skillReading?: unknown;
          skillWriting?: unknown;
        };
      };

      const exerciseName = String(row.exercise_name ?? row.exerciseName ?? "").trim();
      const studentPlayedCount = Number(row.student_played_count ?? row.studentPlayedCount);

      if (!exerciseName || !Number.isFinite(studentPlayedCount)) {
        return null;
      }

      const rawId = row.id;
      const id =
        typeof rawId === "number" || typeof rawId === "string"
          ? rawId
          : String(rawId ?? "").trim() || "-";

      return {
        id,
        uuid: String(row.uuid ?? "").trim(),
        teacherUuid: String(row.teacher_uuid ?? row.teacherUuid ?? "").trim(),
        exerciseName,
        studentPlayedCount,
        capacityPercent: {
          skillListeningSpeaking: Number.isFinite(Number(row.capacity_percent?.skill_listening_speaking ?? row.capacityPercent?.skillListeningSpeaking))
            ? Number(row.capacity_percent?.skill_listening_speaking ?? row.capacityPercent?.skillListeningSpeaking)
            : null,
          skillLangNature: Number.isFinite(Number(row.capacity_percent?.skill_lang_nature ?? row.capacityPercent?.skillLangNature))
            ? Number(row.capacity_percent?.skill_lang_nature ?? row.capacityPercent?.skillLangNature)
            : null,
          skillReading: Number.isFinite(Number(row.capacity_percent?.skill_reading ?? row.capacityPercent?.skillReading))
            ? Number(row.capacity_percent?.skill_reading ?? row.capacityPercent?.skillReading)
            : null,
          skillWriting: Number.isFinite(Number(row.capacity_percent?.skill_writing ?? row.capacityPercent?.skillWriting))
            ? Number(row.capacity_percent?.skill_writing ?? row.capacityPercent?.skillWriting)
            : null,
        },
      };
    })
    .filter(Boolean) as GameRankingRow[];
}

export default function AllGame() {
  const [rows, setRows] = useState<GameRankingRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAllPopupOpen, setIsAllPopupOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedGame, setSelectedGame] = useState<GameRankingRow | null>(null);

  const loadRanking = async () => {
    setIsLoading(true);
    const response = await mainAPI.getAllGamePlayCountRanking();

    if (!response || (response as { status?: string })?.status === "failed") {
      setRows([]);
      setIsLoading(false);
      return;
    }

    setRows(normalizeRankingRows(response));
    setIsLoading(false);
  };

  useEffect(() => {
    void loadRanking();
  }, []);

  useEffect(() => {
    if (!isAllPopupOpen && !selectedGame) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isAllPopupOpen, selectedGame]);

  const topRows = useMemo(() => rows.slice(0, TOP_LIST_LIMIT), [rows]);
  const totalPages = Math.max(1, Math.ceil(rows.length / POPUP_PAGE_SIZE));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * POPUP_PAGE_SIZE;
    return rows.slice(start, start + POPUP_PAGE_SIZE);
  }, [rows, currentPage]);
  const paginationItems = useMemo(
    () => buildPaginationItems(currentPage, totalPages),
    [currentPage, totalPages],
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const renderTable = (tableRows: GameRankingRow[], usePopupIndex = false) => {
    if (isLoading) {
      return <div className="px-4 py-6 text-sm text-[#7a7a7a]">กำลังโหลด...</div>;
    }

    if (tableRows.length === 0) {
      return <div className="px-4 py-6 text-sm text-[#7a7a7a]">ไม่พบข้อมูล</div>;
    }

    return (
      <div className="overflow-x-auto px-4 pb-3">
        <table className="min-w-full border-separate border-spacing-y-0">
          <thead>
            <tr className="text-left text-sm font-semibold text-[#5a5a5a]">
              <th className="w-14 px-4 py-2"></th>
              <th className="px-4 py-2">ชื่อเกม</th>
              <th className="w-40 px-4 py-2 text-right">จำนวนการใช้</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, index) => {
              const rowNumber = usePopupIndex
                ? (currentPage - 1) * POPUP_PAGE_SIZE + index + 1
                : index + 1;
              const isEven = index % 2 === 1;

              return (
                <tr
                  key={`${row.uuid || row.id}_${rowNumber}`}
                  className={isEven ? "bg-[#ECEFF6]" : "bg-[#F7F7F7]"}
                >
                  <td className="px-4 py-2 text-lg font-normal text-[#4E4E4E]">{rowNumber}</td>
                  <td className="px-4 py-2 text-lg font-normal text-[#4E4E4E]">{row.exerciseName}</td>
                  <td className="px-4 py-2 text-right text-lg font-normal text-[#4E4E4E]">
                    <div className="flex items-center justify-end gap-4">
                      <span>{row.studentPlayedCount}</span>
                      <button
                        type="button"
                        onClick={() => setSelectedGame(row)}
                        className="cursor-pointer text-[#0092D7] hover:text-[#0079b3]"
                        title="ข้อมูลเกม"
                        aria-label={`ข้อมูลเกม ${row.exerciseName}`}
                      >
                        <IoInformationCircleOutline size={19} className="mt-0.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div>
        <div className="flex items-center justify-between px-4 pt-4">
          <h1 className="text-lg font-bold text-[#4E4E4E]">เกมยอดนิยมจากคลังต้นแบบ</h1>
          <button
            type="button"
            onClick={() => {
              setCurrentPage(1);
              setIsAllPopupOpen(true);
            }}
            className="text-base font-normal text-[#0092D7] hover:underline cursor-pointer"
          >
            ดูทั้งหมด
          </button>
        </div>

        {renderTable(topRows)}
      </div>

      {isAllPopupOpen ? (
        <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-225 rounded-2xl bg-white shadow-[0_12px_35px_rgba(0,0,0,0.35)]">
            <div className="flex items-center justify-between px-5 py-4">
              <h2 className="text-xl font-semibold text-[#4E4E4E]">เกมทั้งหมด</h2>
              <button
                type="button"
                onClick={() => setIsAllPopupOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#d7dade] text-[#4E4E4E] cursor-pointer transition hover:bg-[#c1c8d1]"
              >
                <IoClose size={22} />
              </button>
            </div>

            <div className="max-h-[80vh] overflow-y-auto pt-2">
              {renderTable(paginatedRows, true)}

              {!isLoading && rows.length > 0 ? (
                <div className="flex justify-center px-4 pb-5 pt-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      className="h-8 min-w-8 rounded border border-[#d9d9d9] px-2 text-sm text-[#4E4E4E] disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                    >
                      ก่อนหน้า
                    </button>

                    {paginationItems.map((item, index) => (
                      item === "ellipsis" ? (
                        <span key={`ellipsis_${index}`} className="px-1 text-[#8a8a8a]">...</span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setCurrentPage(item)}
                          className={`h-8 min-w-8 rounded border px-2 text-sm ${item === currentPage
                            ? "border-[#0092D7] bg-[#0092D7] text-white"
                            : "border-[#d9d9d9] text-[#4E4E4E]"} cursor-pointer`}
                        >
                          {item}
                        </button>
                      )
                    ))}

                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      className="h-8 min-w-8 rounded border border-[#d9d9d9] px-2 text-sm text-[#4E4E4E] disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                    >
                      ถัดไป
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {selectedGame ? (
        <div className="fixed inset-0 z-80 flex items-center justify-center bg-black/45 p-4">
          <div className="flex h-[780px] w-full max-w-172.5 flex-col rounded-2xl bg-white shadow-[0_12px_35px_rgba(0,0,0,0.35)]">
            <div className="flex items-center justify-between px-5 py-4">
              <h2 className="text-xl leading-tight font-semibold text-[#0092D7] wrap-break-word">
                สมรรถนะเฉลี่ยของเกม {selectedGame.exerciseName}
              </h2>
              <button
                type="button"
                onClick={() => setSelectedGame(null)}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#d7dade] text-[#4E4E4E] cursor-pointer transition hover:bg-[#c1c8d1]"
              >
                <IoClose size={22} />
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col px-5 pb-5 pt-0">
              <CapacityOverview
                capacityPercent={selectedGame.capacityPercent}
                variant="popup"
                className="h-full"
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}